/**
 * Pure helpers of the chat screen (unit-tested): merging polled deltas and pages into the cached
 * thread, the `?since=` watermark, and the timeline (day separators, the "Ný skilaboð" divider and runs
 * of messages by the same author).
 */
import type { ChatMessageDTO } from '../../shared/dto/chat';
import { toDateStr } from '../../shared/time';

/** A message as kept in the thread; `local` marks a new message the server has not confirmed yet. */
export type ChatItem = ChatMessageDTO & { local?: 'sending' | 'queued' | 'failed' };

/** Cached thread of one chat (query ['chat-messages', chatId], persisted for offline reading). */
export interface ChatThread {
  messages: ChatItem[];
  /** Older messages exist on the server before the first server message here. */
  hasOlder: boolean;
  /** Messages by others after this are unread (ChatMessagesDTO.unreadAfter). */
  unreadAfter: number;
}

export function byCreated(a: ChatItem, b: ChatItem): number {
  return a.createdAt - b.createdAt || a.id.localeCompare(b.id);
}

/**
 * Merges server rows into the thread by id. The server wins, except over an edit / delete of mine that is
 * still on its way (`inFlight`) when its row is older than my optimistic copy. Deleted messages leave.
 */
export function mergeMessages(prev: readonly ChatItem[], rows: readonly ChatMessageDTO[], inFlight: ReadonlySet<string> = new Set()): ChatItem[] {
  const map = new Map<string, ChatItem>();
  for (const m of prev) map.set(m.id, m);
  for (const row of rows) {
    const cur = map.get(row.id);
    if (cur && inFlight.has(row.id) && row.updatedAt < cur.updatedAt) continue;
    if (row.deletedAt !== null) map.delete(row.id);
    else map.set(row.id, { ...row });
  }
  return [...map.values()].sort(byCreated);
}

/**
 * A whole page from the newest message down replaces what the thread had from the server; my unsent
 * messages (and in-flight edits) stay.
 */
export function resetMessages(prev: readonly ChatItem[], page: readonly ChatMessageDTO[], inFlight: ReadonlySet<string> = new Set()): ChatItem[] {
  const keep = prev.filter((m) => (m.local !== undefined || inFlight.has(m.id)) && !page.some((p) => p.id === m.id && !inFlight.has(m.id)));
  return mergeMessages(keep, page, inFlight);
}

/** Newest updatedAt among server messages (the next poll asks for changes after it), or null. */
export function serverWatermark(list: readonly ChatItem[]): number | null {
  let max: number | null = null;
  for (const m of list) if (!m.local && (max === null || m.updatedAt > max)) max = m.updatedAt;
  return max;
}

/** Oldest message the server sent (cursor for "Sýna eldri skilaboð"). */
export function oldestServerMessage(list: readonly ChatItem[]): ChatItem | null {
  for (const m of list) if (!m.local) return m;
  return null;
}

/** Newest message by someone else (the read marker moves up to it). */
export function newestFromOthers(list: readonly ChatItem[], meId: string): ChatItem | null {
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i]!;
    if (!m.local && m.author.id !== meId) return m;
  }
  return null;
}

export type TimelineEntry =
  | { type: 'day'; key: string; date: string }
  | { type: 'new'; key: string }
  | {
      type: 'msg';
      key: string;
      m: ChatItem;
      mine: boolean;
      /** First of a run by the same author (shows name and avatar). */
      first: boolean;
      /** Last of the run (bubble tail, spacing). */
      last: boolean;
    };

/** Messages by one author further apart than this start a new run. */
export const RUN_GAP_MS = 5 * 60_000;

/**
 * Day separators ('YYYY-MM-DD', Reykjavík = UTC), the "Ný skilaboð" divider before the first message by
 * someone else newer than `newAfter` (my read marker when the chat was opened; null = no divider), and
 * runs of messages by the same author (same day, at most RUN_GAP_MS apart, no divider between).
 */
export function buildTimeline(messages: readonly ChatItem[], meId: string, newAfter: number | null): TimelineEntry[] {
  const out: TimelineEntry[] = [];
  let day: string | null = null;
  let dividerDone = newAfter === null;
  let prev: ChatItem | null = null;
  for (const m of messages) {
    if (m.body === null) continue;
    const d = toDateStr(m.createdAt);
    let breakRun = false;
    if (d !== day) {
      out.push({ type: 'day', key: `day:${d}`, date: d });
      day = d;
      breakRun = true;
    }
    const mine = m.author.id === meId;
    if (!dividerDone && !mine && !m.local && m.createdAt > newAfter!) {
      out.push({ type: 'new', key: 'new' });
      dividerDone = true;
      breakRun = true;
    }
    const first = breakRun || !prev || prev.author.id !== m.author.id || m.createdAt - prev.createdAt > RUN_GAP_MS;
    if (!first) {
      // The run goes on: the message before is no longer its last.
      const before = out[out.length - 1];
      if (before?.type === 'msg') before.last = false;
    }
    out.push({ type: 'msg', key: m.id, m, mine, first, last: true });
    prev = m;
  }
  return out;
}
